import { track } from '@cartwheel/analytics'
import {
  type ActionItem,
  actionItemByIdOptions,
  actionItemReferrerOptionsOptions,
  actionItemsOptions,
  actionItemsQueryKey,
  useCommentOnActionItemMutation,
  useResolveActionItemMutation,
} from '@cartwheel/api/v1'
import {
  Badge,
  Button,
  DataTable,
  DropdownFilter,
  EmptyState,
  ErrorView,
  FilterBar,
  LoadingView,
  Pagination,
  TextInput,
} from '@cartwheel/components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { formatCategory, getBadgeColor } from '../utils/action-item-helpers'
import { ActionItemModal } from './action-item-modal'

type SortDirection = 'asc' | 'desc'
type ActionItemStatusTab = 'active' | 'completed'

const CATEGORY_OPTIONS = [
  { value: 'first_outreach_pending', label: formatCategory('first_outreach_pending') },
  { value: 'outreach_ongoing', label: formatCategory('outreach_ongoing') },
  { value: 'cost_confirmation_needed', label: formatCategory('cost_confirmation_needed') },
  { value: 'cost_under_family_review', label: formatCategory('cost_under_family_review') },
  { value: 'self_schedule_pending', label: formatCategory('self_schedule_pending') },
  { value: 'ready_for_assignment', label: formatCategory('ready_for_assignment') },
]

interface ActionItemsTableProps {
  headerTitle?: string
  organizationId?: string
  showReferrerColumn?: boolean
}

export default function ActionItemsTable({
  headerTitle = 'Team Action Items',
  organizationId,
  showReferrerColumn = false,
}: ActionItemsTableProps) {
  // Pagination and sorting state
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<ActionItemStatusTab>('active')
  const [sortBy, setSortBy] = useState('days_stuck')
  const [sortOrder, setSortOrder] = useState<SortDirection>('desc')
  const perPage = 5

  const [categoryFilters, setCategoryFilters] = useState<string[]>([])
  const [referrerFilters, setReferrerFilters] = useState<string[]>([])
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  // Debounce search: apply 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Modal state
  const [selectedActionItemId, setSelectedActionItemId] = useState<string | null>(null)

  const queryClient = useQueryClient()

  // Composition pattern (useQuery + *Options) because every query here needs
  // an override the named hooks don't surface yet: placeholderData for the
  // list, `enabled` for the conditional fetches. Tracked for cleanup under
  // CCA-1661 once hey-api/openapi-ts #3528 lands.
  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    ...actionItemsOptions({
      query: {
        status: statusFilter,
        page: page,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        organization_id: organizationId,
        categories: categoryFilters.length > 0 ? categoryFilters.join(',') : undefined,
        referrers: referrerFilters.length > 0 ? referrerFilters.join(',') : undefined,
        q: searchQuery || undefined,
      },
    }),
    // Keep previous data visible while re-fetching so filters/dropdowns
    // stay mounted and don't lose their internal state
    placeholderData: (prev) => prev,
  })

  // Fetch referrer options separately so pagination/sort/filter changes don't re-run this query
  const { data: referrerOptionsResponse } = useQuery({
    ...actionItemReferrerOptionsOptions({
      query: {
        status: statusFilter,
        organization_id: organizationId,
      },
    }),
    enabled: showReferrerColumn,
  })

  const {
    data: selectedActionItemResponse,
    isLoading: isSelectedActionItemLoading,
    error: selectedActionItemError,
  } = useQuery({
    ...actionItemByIdOptions({
      path: { id: selectedActionItemId ?? '' },
      query: { source: 'homepage' },
    }),
    enabled: !!selectedActionItemId,
  })

  // Mutation for resolving action items
  const resolveMutation = useResolveActionItemMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: actionItemsQueryKey() })
    },
  })

  // Mutation for adding comments to action items
  const addCommentMutation = useCommentOnActionItemMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: actionItemsQueryKey() })
    },
  })

  // Handle loading state
  if (isLoading) {
    return <LoadingView title="Loading action items..." />
  }

  // Handle error state
  if (error) {
    return (
      <ErrorView title="Error loading action items" message={error.message || 'Unknown error'} />
    )
  }

  // Extract data and metadata from response
  const actionItems = response?.data || []
  const meta = response?.meta || { total: 0, page: 1, per_page: perPage, total_pages: 1 }
  const referrerOptions: { id: string; name: string }[] = referrerOptionsResponse?.data || []

  // Handle empty state
  const isActiveTab = statusFilter === 'active'
  // The active tab is the first thing a brand-new school referrer sees, and they
  // have no referrals yet, so it says what will show up here rather than simply
  // reporting that nothing has. The completed tab keeps the flatter wording: an
  // empty completed list is a plain statement of fact, not a promise.
  const emptyStateTitle = isActiveTab ? 'No action items yet' : 'No completed action items'
  const emptyStateDescription = isActiveTab
    ? "When a referral needs input from your team, it'll appear here with a clear next step."
    : "You don't have any completed action items at this time."
  const isEmptyState = actionItems.length === 0 && page === 1

  const selectedActionItem = selectedActionItemId ? selectedActionItemResponse || null : null

  const handleOpenModal = (actionItemId: string) => {
    setSelectedActionItemId(actionItemId)
  }

  const handleCloseModal = () => {
    setSelectedActionItemId(null)
  }

  const handleMarkCompleted = (note: string) => {
    if (!selectedActionItemId) return

    resolveMutation.mutate(
      {
        path: { id: selectedActionItemId },
        body: note ? { note } : undefined,
      },
      {
        onSuccess: () => {
          handleCloseModal()
        },
      }
    )
  }

  const handleSendNote = (note: string) => {
    if (!selectedActionItemId) return

    addCommentMutation.mutate(
      {
        path: { id: selectedActionItemId },
        body: { note },
      },
      {
        onSuccess: () => {
          handleCloseModal()
        },
      }
    )
  }

  const handleStudentNameClick = (item: ActionItem) => {
    track('ACTION_ITEM_PROFILE_CLICKED', {
      actionItemId: item.id,
      clientId: item.client_id,
      source: 'homepage',
    })
    window.open(`/students/${item.client_id}`, '_blank')
  }

  const handleSort = (column: string) => {
    // If clicking the same column, toggle the sort order
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      // If clicking a new column, set it as the sort column with desc as default
      setSortBy(column)
      setSortOrder('desc')
    }
    setPage(1) // Reset to first page when sorting changes
  }

  const handleCategoryFiltersChange = (values: string[]) => {
    setCategoryFilters(values)
    setPage(1)
  }

  const handleReferrerFiltersChange = (values: string[]) => {
    setReferrerFilters(values)
    setPage(1)
  }

  const handleStatusTabChange = (nextStatus: ActionItemStatusTab) => {
    if (statusFilter === nextStatus) return

    setStatusFilter(nextStatus)
    setPage(1)
    setSelectedActionItemId(null)
  }

  const startItem = (page - 1) * perPage + 1
  const endItem = Math.min(page * perPage, meta.total)

  return (
    <>
      <h2 className="font-ginto-nord-md text-charcoal text-2xl mb-6">{headerTitle}</h2>

      <FilterBar
        rightSlot={
          <div className="relative">
            <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-50 text-sm pointer-events-none" />
            <TextInput
              placeholder="Search for students"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
        }
      >
        <DropdownFilter
          label="Status"
          items={CATEGORY_OPTIONS}
          onChange={handleCategoryFiltersChange}
          showViewModeToggle={false}
        />
        {showReferrerColumn && (
          <DropdownFilter
            label="Referrer"
            items={referrerOptions.map((r) => ({ value: r.id, label: r.name }))}
            onChange={handleReferrerFiltersChange}
            showViewModeToggle={false}
          />
        )}
      </FilterBar>

      <div className="mb-4 border-b border-gray-stroke">
        <div
          role="tablist"
          aria-label="Action item status tabs"
          className="flex items-center gap-6"
        >
          <button
            type="button"
            role="tab"
            aria-selected={isActiveTab}
            className={`-mb-px border-b-2 pb-3 text-base ${
              isActiveTab
                ? 'font-semibold border-forest text-forest'
                : 'border-transparent text-charcoal-50 hover:text-charcoal'
            }`}
            onClick={() => handleStatusTabChange('active')}
          >
            Active
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={!isActiveTab}
            className={`-mb-px border-b-2 pb-3 text-base ${
              isActiveTab
                ? 'border-transparent text-charcoal-50 hover:text-charcoal'
                : 'font-semibold border-forest text-forest'
            }`}
            onClick={() => handleStatusTabChange('completed')}
          >
            Completed
          </button>
        </div>
      </div>

      {isEmptyState ? (
        <EmptyState
          title={emptyStateTitle}
          description={emptyStateDescription}
          illustration={
            // Same glyph the first-login tour's coachmark uses, so the spotlight
            // and the panel it lights up read as one thing.
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-mint-25 text-xl text-forest">
              <i className="fa-regular fa-list-check" aria-hidden="true" />
            </span>
          }
        />
      ) : (
        <div className="w-full border border-gray-stroke rounded-lg bg-white overflow-hidden">
          <DataTable.Root className="table-fixed">
            <DataTable.Header>
              <DataTable.Row>
                <DataTable.HeaderCell
                  className="w-44"
                  sortable
                  sortDirection={sortBy === 'student_name' ? sortOrder : null}
                  onSort={() => handleSort('student_name')}
                >
                  Student Name
                </DataTable.HeaderCell>
                {showReferrerColumn && (
                  <DataTable.HeaderCell className="w-44">Referrer</DataTable.HeaderCell>
                )}
                <DataTable.HeaderCell
                  className="w-52"
                  sortable
                  sortDirection={sortBy === 'category' ? sortOrder : null}
                  onSort={() => handleSort('category')}
                >
                  Status
                </DataTable.HeaderCell>
                <DataTable.HeaderCell className="w-96">Issue Description</DataTable.HeaderCell>
                <DataTable.HeaderCell
                  className="w-32"
                  sortable
                  sortDirection={sortBy === 'days_stuck' ? sortOrder : null}
                  onSort={() => handleSort('days_stuck')}
                >
                  Days in Status
                </DataTable.HeaderCell>
                <DataTable.HeaderCell className="w-44">Action</DataTable.HeaderCell>
              </DataTable.Row>
            </DataTable.Header>
            <DataTable.Body>
              {actionItems.map((item: ActionItem) => (
                <DataTable.Row key={item.id}>
                  <DataTable.Cell label="Student Name">
                    <button
                      type="button"
                      className="text-forest underline hover:opacity-75"
                      onClick={() => handleStudentNameClick(item)}
                    >
                      {item.student_name}
                    </button>
                  </DataTable.Cell>
                  {showReferrerColumn && (
                    <DataTable.Cell label="Referrer" className="text-sm text-charcoal-75">
                      {item.referrer_name ?? '—'}
                    </DataTable.Cell>
                  )}
                  <DataTable.Cell label="Status">
                    <Badge
                      label={formatCategory(item.client_status ?? item.category)}
                      color={getBadgeColor(item.client_status ?? item.category)}
                    />
                  </DataTable.Cell>
                  <DataTable.Cell label="Issue Description" className="text-sm text-charcoal-75">
                    {item.issue_description}
                  </DataTable.Cell>
                  <DataTable.Cell label="Days in Status">{item.days_stuck}</DataTable.Cell>
                  <DataTable.Cell label="Action">
                    {isActiveTab ? (
                      <Button
                        variant="text"
                        onClick={() => handleOpenModal(item.id)}
                        disabled={resolveMutation.isPending}
                        className="!h-auto !border-0 !px-0 !py-0 gap-2 justify-start"
                      >
                        Take action
                        <i className="fas fa-arrow-right text-sm" />
                      </Button>
                    ) : (
                      <div className="flex flex-col items-start">
                        <div className="flex items-center gap-1.5">
                          <i className="fas fa-circle-check text-forest-light text-base" />
                          <span className="text-forest-light text-sm font-medium">Completed</span>
                        </div>
                        {item.resolved_at && (
                          <span className="text-charcoal-50 text-xs font-medium pl-[22px]">
                            {new Date(item.resolved_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        )}
                        {item.resolved_by?.name && (
                          <span className="text-charcoal-50 text-xs pl-[22px]">
                            by {item.resolved_by.name}
                          </span>
                        )}
                      </div>
                    )}
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable.Root>

          {/* Pagination Controls */}
          {meta.total_pages > 1 && (
            <div className="border-t border-gray-stroke p-4 flex justify-end">
              <Pagination
                currentPage={page}
                totalPages={meta.total_pages}
                totalCount={meta.total}
                from={startItem}
                to={endItem}
                hasNextPage={page < meta.total_pages}
                hasPrevPage={page > 1}
                itemsPerPage={perPage}
                onPageChange={setPage}
                onItemsPerPageChange={() => {}}
                showItemsPerPageSelector={false}
              />
            </div>
          )}
        </div>
      )}

      {/* Action Item Modal */}
      {selectedActionItemId && (
        <ActionItemModal
          isOpen={!!selectedActionItemId}
          onClose={handleCloseModal}
          actionItem={selectedActionItem}
          onMarkCompleted={handleMarkCompleted}
          onSendNote={handleSendNote}
          isPending={resolveMutation.isPending || addCommentMutation.isPending}
          isLoading={isSelectedActionItemLoading}
          errorMessage={
            selectedActionItemError
              ? selectedActionItemError.message || 'Unable to load action item details.'
              : null
          }
        />
      )}
    </>
  )
}
